import { Component, input, output } from '@angular/core';

/**
 * Loading Error component for displaying error messages when data fails to load
 * Figma: https://www.figma.com/design/W3VBXvQM2bo98mSNlDSzhi/GivingSpace-Reimagining?node-id=3467-36156
 *
 * This component displays a user-friendly error message with:
 * - Error icon
 * - Title and description
 * - Helpful suggestions list
 * - Action buttons (Refresh and Contact Support)
 * - Reassurance message
 *
 * All text content uses translation keys for internationalization support
 */
@Component({
  selector: 'app-loading-error',
  standalone: false,
  templateUrl: './loading-error.component.html',
  styleUrls: ['./loading-error.component.scss']
})
export class LoadingErrorComponent {
  /**
   * Translation key for the main error title
   * Default: 'error.loading.title'
   */
  titleKey = input<string>('error.loading.title');

  /**
   * Translation key for the error description
   * Default: 'error.loading.description'
   */
  descriptionKey = input<string>('error.loading.description');

  /**
   * Translation key for the suggestions box heading
   * Default: 'error.loading.suggestions_heading'
   */
  suggestionsHeadingKey = input<string>('error.loading.suggestions_heading');

  /**
   * Array of translation keys for suggestion items
   * Default suggestions include: check connection, refresh page, try again, contact support
   */
  suggestionKeys = input<string[]>([
    'error.loading.suggestion.check_connection',
    'error.loading.suggestion.refresh_page',
    'error.loading.suggestion.try_again',
    'error.loading.suggestion.contact_support'
  ]);

  /**
   * Translation key for the primary action button (Refresh)
   * Default: 'error.loading.action.refresh'
   */
  refreshButtonKey = input<string>('error.loading.action.refresh');

  /**
   * Translation key for the secondary action button (Contact Support)
   * Default: 'error.loading.action.contact_support'
   */
  contactButtonKey = input<string>('error.loading.action.contact_support');

  /**
   * Translation key for the reassurance message at the bottom
   * Default: 'error.loading.reassurance'
   */
  reassuranceKey = input<string>('error.loading.reassurance');

  /**
   * Emits when the user clicks the refresh button
   */
  refresh = output<void>();

  /**
   * Emits when the user clicks the contact support button
   */
  contactSupport = output<void>();

  /**
   * Handle refresh button click
   */
  onRefresh(): void {
    this.refresh.emit();
  }

  /**
   * Handle contact support button click
   */
  onContactSupport(): void {
    this.contactSupport.emit();
  }
}
